import React, { useState, useEffect, useRef } from 'react';
import '../../../assests/css/customer/AboutUs.css';
import MyNavbar from '../../../components/Customer/Mynavbar/MyNavbar';
import Footer from '../../../components/Customer/Footer/Footer';

const AboutUs = () => {
  const [counts, setCounts] = useState({
    downloads: 0,
    users: 0,
    subscribers: 0,
    products: 0
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);

  // Stats data
  const stats = [
    { 
      end: 5000, 
      label: 'Downloads',
      key: 'downloads',
      suffix: '+',
      description: "Our platform has been trusted by thousands of users worldwide",
    },
    { 
      end: 2500, 
      label: 'Happy Users',
      key: 'users',
      suffix: '+',
      description: 'Join our growing community of satisfied customers',
    },
    { 
      end: 1200, 
      label: 'Products',
      key: 'products',
      suffix: '+',
      description: "Explore our extensive collection of premium products",
    },
    { 
      end: 98, 
      label: 'Satisfaction',
      key: 'subscribers',
      suffix: '%',
      description: 'Our commitment to excellence reflected in customer satisfaction',
    }
  ];

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  // Counter animation function
  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    stats.forEach((stat) => {
      let currentStep = 0;
      const increment = stat.end / steps;

      const timer = setInterval(() => {
        currentStep++;
        const value = Math.min(Math.floor(increment * currentStep), stat.end);
        
        setCounts((prev) => ({
          ...prev,
          [stat.key]: value
        }));

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  };

  // Team members data
  const teamMembers = [
    { 
      name: 'Sarah Mitchell', 
      role: 'Fashion Director',
      imageUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop',
      email: 'sarah@fashion.com'
    },
    { 
      name: 'James Chen', 
      role: 'Head of Styling',
      imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
      email: 'james@fashion.com'
    },
    { 
      name: 'Emma Rodriguez', 
      role: 'Creative Designer',
      imageUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop',
      email: 'emma@fashion.com'
    },
    { 
      name: 'Michael Stevens', 
      role: 'Brand Manager',
      imageUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=300&fit=crop',
      email: 'michael@fashion.com'
    },
    { 
      name: 'Sophia Anderson', 
      role: 'Collection Curator',
      imageUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop',
      email: 'sophia@fashion.com'
    },
    { 
      name: 'David Park', 
      role: 'Customer Experience Lead',
      imageUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&h=300&fit=crop',
      email: 'david@fashion.com'
    }
  ];

  // Services data
  const services = [
    { 
      icon: '🛍️', 
      title: 'Premium Shopping',
      description: 'Curated collection of the finest fashion items from around the world',
      color: '#3498db'
    },
    { 
      icon: '✨', 
      title: 'Style Consultation',
      description: 'Expert fashion advice to help you find your perfect style',
      color: '#9b59b6'
    },
    { 
      icon: '🚚', 
      title: 'Fast Delivery',
      description: 'Quick and secure shipping to bring fashion to your doorstep',
      color: '#e74c3c'
    }
  ];

  return (
    <>
      <MyNavbar />
      <div className="about-us-page">
        {/* ==================== HERO SECTION ==================== */}
        <section className="hero-section">
          <div className="hero-background">
            <img 
              src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&h=600&fit=crop" 
              alt="Ecommerce Shopping"
              className="hero-bg-image"
            />
            <div className="hero-overlay-dark"></div>
          </div>
          <div className="hero-overlay">
            <h1>About Us</h1>
            <p>
              Your Premier Destination for Fashion Excellence - Where Style Meets Quality.
              <br />
              Bringing you the latest trends and timeless classics in fashion ecommerce.
            </p>
          </div>
        </section>

        {/* ==================== ABOUT COMPANY SECTION ==================== */}
        <section className="about-company">
          <div className="container">
            <div className="about-content">
              <div className="about-text">
                <h2>About Our Brand</h2>
                <p>
                  Welcome to our fashion ecommerce store, where we bring you the finest 
                  collection of clothing, accessories, and lifestyle products. We are 
                  dedicated to providing high-quality fashion items that blend contemporary 
                  style with timeless elegance.
                </p>
                <p>
                  Our store offers a curated selection of trending outfits, premium fabrics, 
                  and exclusive designs. From casual wear to formal attire, we cater to every 
                  fashion need. With secure payment options, fast delivery, and exceptional 
                  customer service, we ensure a seamless shopping experience for fashion 
                  enthusiasts worldwide.
                </p>
              </div>

              <div className="about-image-section">
                <div className="decorative-bg">
                  <div className="floating-shape shape-1"></div>
                  <div className="floating-shape shape-2"></div>
                  <div className="floating-shape shape-3"></div>
                  <div className="gradient-orb orb-1"></div>
                  <div className="gradient-orb orb-2"></div>
                </div>
                
                <div className="store-showcase-card">
                  <div className="card-glass-effect"></div>
                  
                  <div className="store-header">
                    <div className="store-badge-container">
                      <div className="badge-glow"></div>
                      <div className="store-badge">
                        <span className="badge-emoji">👗</span>
                        <span className="badge-title">Fashion Store</span>
                        <span className="verified-check">✓</span>
                      </div>
                    </div>
                    <div className="trust-indicators">
                      <div className="trust-item">
                        <span className="trust-icon">🌟</span>
                        <span className="trust-text">Premium Quality</span>
                      </div>
                      <div className="trust-item">
                        <span className="trust-icon">🚚</span>
                        <span className="trust-text">Fast Shipping</span>
                      </div>
                    </div>
                  </div>

                  <div className="store-image-showcase">
                    <div className="image-main">
                      <img 
                        src="https://images.unsplash.com/photo-1445205170230-053b83016050?w=700&h=500&fit=crop" 
                        alt="Fashion Store"
                      />
                      <div className="image-gradient-overlay"></div>
                      <div className="sparkle sparkle-1">✨</div>
                      <div className="sparkle sparkle-2">✨</div>
                      <div className="sparkle sparkle-3">✨</div>
                    </div>
                    
                    <div className="store-features">
                      <div className="feature-item feature-1">
                        <div className="feature-icon">🎨</div>
                        <div className="feature-content">
                          <span className="feature-number">500+</span>
                          <span className="feature-label">Brands</span>
                        </div>
                      </div>
                      <div className="feature-item feature-2">
                        <div className="feature-content">
                          <span className="feature-number">10K+</span>
                          <span className="feature-label">Products</span>
                        </div>
                      </div>
                      <div className="feature-item feature-3">
                        <div className="feature-content">
                          <span className="feature-number">4.9/5</span>
                          <span className="feature-label">Rating</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="store-metrics">
                    <div className="metric-card">
                      <div className="metric-icon-wrapper">
                        <span className="metric-icon">👥</span>
                      </div>
                      <div className="metric-info">
                        <span className="metric-value">50K+</span>
                        <span className="metric-label">Happy Customers</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-icon-wrapper">
                        <span className="metric-icon">🎁</span>
                      </div>
                      <div className="metric-info">
                        <span className="metric-value">2K+</span>
                        <span className="metric-label">Daily Orders</span>
                      </div>
                    </div>
                  </div>

                  <div className="store-cta">
                    <button className="explore-store-btn">
                      <span>Explore Collection</span>
                      <span className="btn-arrow">→</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== WHAT WE DO SECTION ==================== */}
        <section className="what-we-do">
          <div className="container">
            <h2>What We Do</h2>
            <div className="services-grid">
              {services.map((service, index) => (
                <div key={index} className="service-card-modern">
                  <div className="card-bg-pattern"></div>
                  <div 
                    className="service-icon-modern" 
                    style={{ 
                      background: `linear-gradient(135deg, ${service.color}, ${service.color}dd)` 
                    }}
                  >
                    <span>{service.icon}</span>
                    <div className="icon-glow"></div>
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <div className="card-arrow">→</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ==================== STATISTICS SECTION ==================== */}
        <section ref={statsRef} className="stats-section">
          <div className="stats-header">
            <h2>Our Achievements</h2>
            <p>Numbers that speak for our excellence and commitment</p>
          </div>
          <div className="stats-container">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-number">
                  {counts[stat.key]}{stat.suffix}
                </div>
                <div className="stat-label">{stat.label}</div>
                <p className="stat-description">{stat.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ==================== MEET OUR TEAM SECTION ==================== */}
        <section className="meet-team">
          <div className="container">
            <h2>Meet Our Team</h2>
            <p className="team-description">
              Our passionate team of fashion experts and ecommerce professionals brings 
              years of experience in curating the perfect styles for you.
            </p>
            
            <div className="team-grid">
              {teamMembers.map((member, index) => (
                <div key={index} className="team-card-elegant">
                  <div className="card-decorative-corner corner-tl"></div>
                  <div className="card-decorative-corner corner-tr"></div>
                  <div className="card-decorative-corner corner-bl"></div>
                  <div className="card-decorative-corner corner-br"></div>
                  
                  <div className="team-card-content">
                    <div className="team-image-elegant">
                      <div className="image-hexagon">
                        <img src={member.imageUrl} alt={member.name} />
                      </div>
                      <div className="status-badge">
                        <span className="status-dot"></span>
                        <span className="status-text">Available</span>
                      </div>
                    </div>
                    
                    <div className="team-member-info">
                      <h3>{member.name}</h3>
                      <p className="member-role">{member.role}</p>
                      <p className="member-email">{member.email}</p>
                    </div>
                    
                    <div className="divider-line"></div>
                    
                    <div className="team-social-links">
                      <a href="#" className="social-link linkedin" aria-label="LinkedIn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>
                      <a href="#" className="social-link email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                        </svg>
                      </a>
                      <a href="#" className="social-link phone" aria-label="Phone">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default AboutUs;